import { el, fragment } from '../dom.js';
import { admin, adminRaw } from '../admin-api.js';
import { t, formatDate } from '../i18n.js';

// A host, never the full URL, the same reason the CLI review line shows one:
// what tells a reviewer at a glance that a link is the site's own nav shop
// rather than this event's, and the full query string would not fit the row.
function ticketHost(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

// Grouped by source, because that is how the review is actually done: a
// source's programme is homogeneous, so a reviewer scans a block and takes it
// whole. `approve-source` in the CLI already encodes that; this is it plus eyes.
//
// Classification sits in the same control, because classification is what
// blocks: zakk's 52 items are all refused with no_classification, its programme
// being mixed on purpose. Without setting a facet on a selection, this screen
// would be a wall of blocked items and no way to unblock them.
export async function queuePanel({ reload }) {
  const data = await admin('GET', '/crawl/queue');
  const groups = data?.groups ?? [];

  if (!groups.length) return el('div', { class: 'pub-empty' }, t.crawlEmptyQueue);

  // Where the work is, first. A source with 97 rows that can none of them be
  // promoted is not where a reviewer starts, and sorting by size alone put
  // exactly that at the top — Club Bahnhof Ehrenfeld states only a doors time,
  // so all 97 are permanently blocked until the source learns to ground one.
  const ranked = [...groups].sort((a, b) => approvable(b) - approvable(a) || b.items.length - a.items.length);
  return fragment(ranked.map((group) => sourceGroup(group, reload)));
}

const approvable = (group) => (group.needsVenue ? 0 : group.items.filter((item) => !item.blocked).length);

function sourceGroup(group, reload) {
  const boxes = new Map();

  const list = el('ul', { class: 'card-list' }, group.items.map((item) => {
    const box = el('input', { type: 'checkbox' });
    // A row nobody can promote is not preselectable: checking it would make the
    // approve button report a refusal the reviewer could have been spared.
    box.disabled = !!item.blocked || group.needsVenue;
    boxes.set(item.id, box);
    return el('li', {}, el('div', { class: 'card' }, [
      el('div', { class: 'row' }, [
        box,
        el('h4', { class: 'card-title' }, item.title ?? '—'),
        // Provenance is checkable in one click or it is decoration. The source
        // key is the group's heading already, so the link carries a verb.
        item.url
          ? el('a', { class: 'row-end', href: item.url, target: '_blank', rel: 'noreferrer' }, t.crawlOpenSource)
          : el('span', { class: 'row-end card-meta' }, '—'),
      ]),
      el('p', { class: 'card-meta' }, [
        formatDate(item.localDate),
        ` · ${item.localStartTime ?? t.crawlNoStartTime}`,
        item.yearInferred ? ` · ${t.crawlYearGuessed}` : '',
        // The ICS fact and the page-wide guess are different claims and read
        // differently, because only the fact is published as the subtitle.
        item.location ? ` · ${t.crawlRoom(item.location)}` : '',
        item.locationHint ? ` · ${t.crawlRoomHint(item.locationHint)}` : '',
        // Published exactly as staged, with no review step of their own: a
        // wrong ticket link is the most expensive error this feature makes,
        // and it had no review surface at all until now.
        item.priceFree ? ` · ${t.crawlPriceFree}` : (item.priceText ? ` · ${t.crawlPrice(item.priceText)}` : ''),
        item.ticketUrl ? ` · ${t.crawlTicket(ticketHost(item.ticketUrl))}` : '',
      ].join('')),
    ]));
  }));

  // The explanation once per group, never per row. "KEINE ZEIT" already stands
  // on every affected line; repeating the sentence behind it ninety-seven times
  // is how a group becomes unreadable and the rows that ARE approvable get lost.
  const blockedCount = group.items.filter((item) => item.blocked === 'no_time').length;

  const facets = el('input', {
    type: 'text', class: 'input', placeholder: t.crawlFacets,
    value: group.facets.join(','),
  });
  const tags = el('input', { type: 'text', class: 'input', placeholder: t.crawlTags });
  const status = el('p', { class: 'card-meta' }, '');

  const selected = () => [...boxes.entries()].filter(([, box]) => box.checked).map(([id]) => id);
  const split = (value) => value.split(',').map((part) => part.trim()).filter(Boolean);

  const selectAll = el('button', { type: 'button', class: 'btn' }, t.crawlSelectAll);
  selectAll.addEventListener('click', () => {
    for (const box of boxes.values()) if (!box.disabled) box.checked = true;
  });

  const approve = el('button', { type: 'button', class: 'btn btn-primary' }, t.crawlApprove(group.items.length));
  approve.addEventListener('click', async () => {
    const ids = selected();
    if (!ids.length) {
      // A control that does nothing and says nothing reads as a broken backend,
      // and this one has two quite different reasons to refuse. The source-level
      // one is the reason the whole queue sat at zero published events: every
      // row is unapprovable until an institution exists, and the group notice
      // saying so is above the fold on a card that opens ninety-seven rows deep.
      status.textContent = group.needsVenue ? t.crawlNeedsVenue : t.crawlNothingSelected;
      return;
    }
    approve.disabled = true;
    const response = await adminRaw('POST', '/crawl/items/approve', {
      ids, facets: split(facets.value), tags: split(tags.value),
    });
    const body = await response.json();
    // Item by item: a batch where one row refuses promotes the rest and names
    // which refused, or a reviewer approving fifty events learns only that
    // "something" was wrong.
    status.textContent = [
      t.crawlApproved(body.ok?.length ?? 0),
      body.failed?.length ? ` · ${body.failed.map((f) => `${f.id}: ${f.code}`).join(', ')}` : '',
    ].join('');
    await reload();
  });

  const reject = el('button', { type: 'button', class: 'btn' }, t.crawlReject);
  const rejectReason = el('input', { type: 'text', class: 'input', placeholder: t.crawlRejectReason });
  reject.addEventListener('click', async () => {
    const ids = [...boxes.entries()].filter(([, box]) => box.checked).map(([id]) => id);
    if (!ids.length) { status.textContent = t.crawlNothingSelected; return; }
    reject.disabled = true;
    await adminRaw('POST', '/crawl/items/reject', { ids, reason: rejectReason.value.trim() || null });
    await reload();
  });

  // Rejecting is possible for rows that cannot be promoted — an item with no
  // start time is exactly the kind that should leave the queue — so those boxes
  // are enabled for this control even though approval refuses them.
  const rejectable = el('button', { type: 'button', class: 'btn' }, t.crawlSelectBlocked);
  rejectable.addEventListener('click', () => {
    for (const box of boxes.values()) box.disabled = false;
  });

  // Closed by default. The summary carries the count and how many are actually
  // approvable, which is the whole decision about whether to open it — and one
  // source alone runs to ninety-seven rows.
  return el('details', { class: 'card' }, [
    el('summary', {}, [
      `${group.label} · ${t.crawlPending(group.items.length)}`,
      approvable(group) ? ` · ${t.crawlPromotable(approvable(group))}` : '',
    ].join('')),
    blockedCount ? el('p', { class: 'card-meta' }, `${t.crawlNoTimeCount(blockedCount)} — ${t.crawlNoTime}`) : '',
    // The source-level blocks, said once for the group rather than repeated on
    // every row. Creating an institution stays a typed command: it allocates a
    // nickname in a namespace where names are never reissued, which is the one
    // irreversible step in this pipeline.
    group.needsVenue
      ? el('div', { class: 'pub-empty' }, [
          el('p', {}, t.crawlNeedsVenue),
          el('p', {}, el('code', {}, t.crawlVenueCommand(group.key))),
        ])
      : '',
    group.needsClassification ? el('p', { class: 'card-meta' }, t.crawlNeedsClassification) : '',
    list,
    el('div', { class: 'row' }, [selectAll, rejectable]),
    el('div', { class: 'row' }, [facets, tags, approve]),
    el('div', { class: 'row' }, [rejectReason, reject]),
    status,
  ].filter(Boolean));
}
